import React, { useState, useEffect, useContext } from "react";
import { Badge, Card } from "react-bootstrap";
import { HiOutlineDocumentReport } from 'react-icons/hi';
import MaterialTable, { MTableToolbar, MTableGroupRow } from 'material-table';
import icons from 'MaterialTableIcons';
import { Link, useParams } from 'react-router-dom';
import { chartSeriesColor } from 'Utils/styleTokens';
import { flattenObj, getRiskScore, RISK_LEVELS, truncateCenter } from 'Utils';
import AppContext from 'Utils/AppContext';
import { filterForDisplay } from 'Utils/entityLevels';
import { 
  engineLabels, 
  EngineIcon,
} from 'Utils';
import { contrastColor } from 'contrast-color';
import { uiLabel } from "Utils";
import DiscoveryTooltip from "DiscoveryTooltip";

const SourceIdentifierColumn = (props) => {
  const label = props.label || props.identifier || '-';
  if (props.id == null) {
    return (
      <span className='pl-3 pt-3 pb-3'>{truncateCenter(label, 32, '...')}</span>
    );
  }
  return (
    <span className='pl-3 pt-3 pb-3'>
      <DiscoveryTooltip text='View risk details for this Data Source' placement='top'>
        <Link to={`/landscape/riskprofile/${encodeURIComponent(props.id)}`}>
          {truncateCenter(label, 32, '...')}
        </Link>
      </DiscoveryTooltip>
    </span>
  );
};


const RiskCell = (props) => {
  const [riskScore, setRiskScore] = useState(1);
  const [color, setColor] = useState('#fff');
  const [label, setLabel] = useState('');

  useEffect(() => {
    const riskScoreValue = props.riskScore;
    const colorValue = RISK_LEVELS[riskScoreValue].color;
    const labelValue = RISK_LEVELS[riskScoreValue].label;

    setRiskScore(riskScoreValue);
    setColor(colorValue);
    setLabel(labelValue);
  }, [props.findings]);

  return (
    <div style={{ padding: '2px' }}>
      <div
        style={{
          color: contrastColor({ bgColor: color }),
          backgroundColor: color,
          textAlign: 'center',
          fontWeight: '500',
          ...!props.percent && ({ fontSize: '12px' }),
          borderRadius:'5px'
        }}
        className="pl-4 pr-4 pt-3 pb-3 discovery-badge-db-tables"
      >
        { label }
      </div>
    </div>
  )
}

const FindingsColumn = (props) => {
  const findings = props.findings;
  const colorGradientArray = Array.from({ length: 10 }, (_, i) => chartSeriesColor(i));

  return (
    <div className='ls-findings-cell'>
      <div>
        { findings && findings.slice(0, 10).map((finding, i) => (
          <Badge
            className='landscape-badge text-uppercase' 
            style={{ 
              background: colorGradientArray[i],
              color: '#fff',
              fontSize:'12px'
            }}
            key={i}
          >
            { uiLabel(finding.entity) }
          </Badge>
        ))}
      </div>
    </div>
  );
}

const LandscapeTable = (props) => {
  const [data, setData] = useState([]);
  const tableRef = React.createRef();
  const { source } = useParams();
  const { entityLevels } = useContext(AppContext);


  useEffect(() => {
    const landscapeData = props.landscapeData || [];
    const dataValue = [];
    for (let i = 0; i < landscapeData.length; i++) {
      const obj = { ...landscapeData[i] };
      const hasDiscovery = obj.latestDiscoveryAt != null || obj.latestDiscoveryId != null;
      // Drop HIDDEN entities install-wide so both the badge cell and the computed risk score
      // ignore them. A server-precomputed latestRiskScore is kept as-is (backend-derived).
      const findings = filterForDisplay(obj.findings || [], entityLevels);
      obj.findings = findings;
      const riskScore = typeof obj.latestRiskScore === 'number' ? obj.latestRiskScore : getRiskScore(findings);
      const hasRiskData = typeof obj.latestRiskScore === 'number' || findings.length > 0;
      if (!hasDiscovery && !hasRiskData) continue;
      obj.riskScore = riskScore;
      dataValue.push(obj);
    }
    dataValue.sort((a, b) => (b.riskScore || 1) - (a.riskScore || 1));
    setData(dataValue);
  }, [props.landscapeData, entityLevels]);

  const COLUMNS = [
    {
      title: 'RISK LEVEL',
      field: 'riskScore',
      width: '125px',
      defaultSort: 'desc',
      lookup: flattenObj(RISK_LEVELS, 'label'),
      headerStyle: {
        fontFamily: 'Rubik',
        textAlign: 'center'
      },
      cellStyle: {
        padding: 0,
        fontFamily: 'Rubik'
      },
      render: (rowData) => (
        <RiskCell
          findings={rowData.findings}
          riskScore={rowData.riskScore}
        />
      )
    }
    ,
    {
      title: 'DATA SOURCE',
      field: 'connections',
      width: '250px',
      headerStyle: {
        fontFamily: 'Rubik'
      },
      cellStyle: {
        fontFamily: 'Rubik',
        paddingLeft: 0,
        paddingRight: 0
      },
      render: (rowData) => (
        <SourceIdentifierColumn
          id={rowData.id}
          label={(rowData.connections && rowData.connections[0]) || rowData.name || '-'}
          source={rowData.source}
        />
      )
    },
    {
      title: source === 's3' ? 'S3 BUCKET' : `${source} IDENTIFIER`,
      field: 'identifier',
      width: '250px',
      headerStyle: {
        fontFamily: 'Rubik'
      },
      cellStyle: {
        fontFamily: 'Rubik',
        paddingLeft: 0,
        paddingRight: 0
      },
      render: (rowData) => (
        <SourceIdentifierColumn
          id={rowData.id}
          label={(rowData.connections && rowData.connections[0]) || rowData.name || '-'}
          source={rowData.source}
        />
      )
    },
    ...(source !== 's3' ? [{
      title: 'ENGINE',
      field: 'engine',
      width: '130px',
      headerStyle: {
        fontFamily: 'Rubik',
        textAlign: 'left',
      },
      cellStyle: {
        fontFamily: 'Rubik',
        textAlign: 'left',
      },
      render: (rowData) => {
        const engine = rowData.engine;

        return (
          <span>
            <EngineIcon engine={engine} /> {' '}  {engineLabels[engine]} 
          </span>
        );
      }
    }] : []),
    ...(source !== 's3' ? [{
      title: 'REGION',
      field: 'region',
      width: '130px',
      headerStyle: {
        fontFamily: 'Rubik',
        textAlign: 'left'
      },
      cellStyle: { 
        fontFamily: 'Rubik',
        textAlign: 'left',
      },
      render: (rowData) => {

        return (
          <div>
            {rowData.region ? rowData.region: '-'}
          </div>
        );
      }
    }] : []),
    {
      title: 'TOP 10 FINDINGS',
      field: 'qtyOfFindings',
      defaultSort: 'desc',
      filtering: false,
      grouping: false,
      width: '350px',
      headerStyle: {
        fontFamily: 'Rubik',
        textAlign: 'left',
      },
      cellStyle: {
        fontFamily: 'Rubik',
        fontSize: '12px'
      },
      render: (rowData) => (
        <FindingsColumn
          findings={rowData.findings}
        />
      )
    }
  ];

  return (
    <Card className="entity-panel-table landscape-table-container">
      <MaterialTable
        className='position-relative'
        isLoading={props.fetchingData}
        components={{
          Toolbar: props => (
            <div className="entity-panel-table-header d-flex">
              <div className="text-left w-25 widget-header p-3">
                <HiOutlineDocumentReport className="mr-2 widget-header-icon" /> 
                <span>Risk Score By Data Source</span>
              </div>
              <div className='d-flex justify-content-end w-75'>
                <MTableToolbar { ...props } />
              </div>
            </div>
          ),
          GroupRow: props => (
            <div className='w-100'>
              
              <MTableGroupRow { ...props } />
            </div>
          )
        }}
        tableRef={tableRef}
        icons={icons}
        columns={COLUMNS}
        data={data}
        options={{
          showTitle: false,
          pageSize: props.pageSize,
          tableLayout: 'fixed',
          filtering: true
        }}
      />
    </Card>
  );
}

export default LandscapeTable;